import { EventType, type AccountInfo, type AuthenticationResult, type EventMessage, type IPublicClientApplication } from '@azure/msal-browser'

/**
 * Which signed-in account the portal acts as.
 *
 * MSAL caches every account that has signed in on this browser. The portal used to take the
 * FIRST one (`getAllAccounts()[0]`) for API tokens and the profile shown, so a browser holding
 * two accounts (an AlphaBOLD one and a customer test one, say) could call the API as an account
 * other than the one the user just signed in with. Now the account a sign-in returns is
 * recorded as ACTIVE, and that is the only one used.
 *
 * Never guesses: with several cached accounts and none active, this returns null and the caller
 * sends the user to the account picker rather than picking one for them.
 */
export function resolveActiveAccount(instance: Pick<IPublicClientApplication, 'getActiveAccount' | 'getAllAccounts' | 'setActiveAccount'>): AccountInfo | null {
  const active = instance.getActiveAccount()
  if (active) return active
  const all = instance.getAllAccounts()
  if (all.length === 1) {
    // Exactly one: unambiguous, so it becomes the active account.
    instance.setActiveAccount(all[0]!)
    return all[0]!
  }
  return null
}

/** More than one cached account and none chosen: the user must pick. */
export function needsAccountChoice(instance: Pick<IPublicClientApplication, 'getActiveAccount' | 'getAllAccounts'>): boolean {
  return !instance.getActiveAccount() && instance.getAllAccounts().length > 1
}

/** The events whose payload is the account the user just signed in or acquired a token as. */
const SIGN_IN_EVENTS: ReadonlySet<string> = new Set([
  EventType.LOGIN_SUCCESS,
  EventType.ACQUIRE_TOKEN_SUCCESS,
  EventType.SSO_SILENT_SUCCESS,
])

/** Record the signed-in account as active whenever MSAL reports a successful sign-in. */
export function trackActiveAccount(instance: Pick<IPublicClientApplication, 'addEventCallback' | 'setActiveAccount'>): void {
  instance.addEventCallback((event: EventMessage) => {
    const account = (event.payload as AuthenticationResult | null)?.account
    if (SIGN_IN_EVENTS.has(event.eventType) && account) instance.setActiveAccount(account)
  })
}
